/**
 * Per-user shell preferences (spec M0-F4 §4.2, §6 `usePreference`).
 *
 * monday's shell state is server-side — the pane's collapsed state, the last view per board and
 * the collapsed groups all survive a different browser — so the read path is
 * `shell.bootstrap().preferences` and the write path is `preferences.set`.
 *
 * **The write path, round 10l (P-160).** F8 shipped `preferences.set` / `.setMany`, and
 * `useBootstrap` now calls `configurePreferenceTransport` with a transport that sends it, so a
 * preference set in one browser is read back by the next one (F4 §4.2 S2/X4). `localStorage`
 * stays as the *mirror* a reload reads before the bootstrap answers — never as the only copy.
 *
 * **The mirror is per key (P-170).** `writeLocal` used to persist the whole store, so any write
 * that happened before `hydratePreferences` had run rewrote every other key with its default —
 * one "remember the workspace" effect was enough to lose the collapsed pane, the pane width, the
 * expanded folders, the Content sort, *Do not disturb* and *Unread only*. It now merges the one
 * key it was given into whatever is already stored, and a write made before hydration holds its
 * remote send until the server's copy has been layered in, so the two can never race.
 */
import { useCallback, useSyncExternalStore } from 'react';

export type ThemeName = 'light' | 'dark' | 'black';

export interface WorkingStatus {
  text: string;
  emoji: string | null;
  until: string | null;
}

/** The key set of §4.2, with the value shape each key validates against. */
export interface ShellPreferences {
  'shell.theme': ThemeName;
  'shell.sidebarExpanded': boolean;
  'shell.sidebarWidth': number;
  'shell.lastWorkspaceId': string | null;
  'shell.expandedFolderIds': Record<string, string[]>;
  'shell.lastBoardIdByWorkspace': Record<string, string>;
  /** B10 — the sidebar Content section's `Sort by alphabetical order` toggle. */
  'shell.contentSort': 'position' | 'alphabetical';
  'board.lastViewId': Record<string, string>;
  'board.collapsedGroupIds': Record<string, string[]>;
  'notifications.unreadOnly': boolean;
  'inbox.tab': string;
  'inbox.boardFilter': string;
  'inbox.show': 'unread' | 'all';
  'status.doNotDisturb': boolean;
  'status.workingStatus': WorkingStatus | null;
}

export type PreferenceKey = keyof ShellPreferences;

export const THEME_NAMES: readonly ThemeName[] = ['light', 'dark', 'black'];

export function isThemeName(value: unknown): value is ThemeName {
  return typeof value === 'string' && (THEME_NAMES as readonly string[]).includes(value);
}

export const PREFERENCE_DEFAULTS: ShellPreferences = {
  'shell.theme': 'light',
  'shell.sidebarExpanded': true,
  'shell.sidebarWidth': 279,
  'shell.lastWorkspaceId': null,
  'shell.expandedFolderIds': {},
  'shell.lastBoardIdByWorkspace': {},
  'shell.contentSort': 'position',
  'board.lastViewId': {},
  'board.collapsedGroupIds': {},
  'notifications.unreadOnly': false,
  'inbox.tab': 'all_updates',
  'inbox.boardFilter': 'all',
  'inbox.show': 'unread',
  'status.doNotDisturb': false,
  'status.workingStatus': null,
};

export const PREFERENCE_KEYS = Object.keys(PREFERENCE_DEFAULTS) as PreferenceKey[];

/** The pane resize range (F4 §4.2, ASSUMED — open question 100). */
export const SIDEBAR_WIDTH_MIN = 240;
export const SIDEBAR_WIDTH_MAX = 520;
export const SIDEBAR_WIDTH_DEFAULT = 279;

export function clampSidebarWidth(width: number): number {
  if (!Number.isFinite(width)) return SIDEBAR_WIDTH_DEFAULT;
  return Math.min(Math.max(Math.round(width), SIDEBAR_WIDTH_MIN), SIDEBAR_WIDTH_MAX);
}

/** X1: an unknown key is rejected and a value of the wrong shape falls back to the default. */
export function isPreferenceKey(key: string): key is PreferenceKey {
  return Object.prototype.hasOwnProperty.call(PREFERENCE_DEFAULTS, key);
}

function sameShape(a: unknown, b: unknown): boolean {
  if (a === null || b === null) return true;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  return typeof a === typeof b;
}

export function coercePreferences(raw: unknown): ShellPreferences {
  const merged: ShellPreferences = { ...PREFERENCE_DEFAULTS };
  if (!raw || typeof raw !== 'object') return merged;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!isPreferenceKey(key)) continue;
    if (!sameShape(value, PREFERENCE_DEFAULTS[key])) continue;
    (merged as unknown as Record<string, unknown>)[key] = value;
  }
  if (typeof merged['shell.sidebarWidth'] === 'number') {
    merged['shell.sidebarWidth'] = clampSidebarWidth(merged['shell.sidebarWidth']);
  }
  return merged;
}

/* --------------------------------------------------------------- the store */

const LOCAL_STORAGE_KEY = 'elizmonday.shell.preferences';
const DEBOUNCE_MS = 400;

type Listener = () => void;

let state: ShellPreferences = { ...PREFERENCE_DEFAULTS };
const listeners = new Set<Listener>();
const pending = new Map<PreferenceKey, { timer: ReturnType<typeof setTimeout> }>();
/**
 * P-170: false until `shell.bootstrap` has answered. While it is false the store holds
 * `PREFERENCE_DEFAULTS`, so a value read out of it is not the user's — only the key actually
 * written is persisted, and its remote send waits for the server's copy to be merged in.
 */
let hydrated = false;
const queuedRemote = new Set<PreferenceKey>();

function emit() {
  for (const listener of listeners) listener();
}

function readLocal(): Partial<ShellPreferences> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<ShellPreferences>) : {};
  } catch {
    // Private windows and blocked site data both throw. A shell with default preferences is a
    // correct shell, so this can never be fatal.
    return {};
  }
}

/**
 * P-170: **one key**, merged into whatever this browser already stored. Persisting the whole
 * store meant persisting `PREFERENCE_DEFAULTS` for every key the user had not touched in this
 * tab yet, which is exactly what a write before hydration does.
 */
function writeLocal<K extends PreferenceKey>(key: K, value: ShellPreferences[K]) {
  if (typeof window === 'undefined') return;
  try {
    const stored = readLocal();
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({ ...stored, [key]: value }));
  } catch {
    // Same as above: losing the mirror costs a reload's worth of state, nothing more.
  }
}

/**
 * The remote write — `preferences.set`, installed by `useBootstrap` (P-160). It stays a `let`
 * rather than an import so this store has no dependency on React or on the tRPC client, which
 * is what lets it be read synchronously from anywhere.
 */
type PreferenceTransport = <K extends PreferenceKey>(key: K, value: ShellPreferences[K]) => void;

/**
 * Every transport currently installed, newest last; the newest is the one that writes.
 *
 * A **set**, not a single slot, because `useBootstrap` has more than one caller (`AppShell` and
 * `ShellOverlays`) and a single slot with an unconditional cleanup meant that *closing the
 * notifications pane* silently unwired the whole app's preference writes: the sidebar's collapse
 * reached the server, and everything a user changed after opening an overlay did not. The last
 * caller to unmount takes the last transport with it, which is the only time there should be
 * none.
 */
const transports = new Set<PreferenceTransport>();

function setRemotePreference<K extends PreferenceKey>(key: K, value: ShellPreferences[K]): void {
  const active = [...transports].pop();
  active?.(key, value);
}

/** Installs a transport and hands back the disposer that removes **that** one. */
export function configurePreferenceTransport(transport: PreferenceTransport | null): () => void {
  if (transport === null) {
    transports.clear();
    return () => undefined;
  }
  transports.add(transport);
  return () => {
    transports.delete(transport);
  };
}

/** Seeds the store from `shell.bootstrap`, then layers this browser's unsynced writes on top. */
export function hydratePreferences(fromServer: unknown) {
  state = coercePreferences({ ...coercePreferences(fromServer), ...readLocal() });
  hydrated = true;
  // P-170: anything written while the bootstrap was still in flight is sent now, once, with the
  // value the merge above settled on.
  for (const key of queuedRemote) setRemotePreference(key, state[key]);
  queuedRemote.clear();
  emit();
}

export function isPreferencesHydrated(): boolean {
  return hydrated;
}

/**
 * Whether the server's copy has been layered in yet — as a **subscribed** value, so a screen
 * that must not act on a default can wait for the real one.
 *
 * T-9h needs exactly this: `/` resolves the landing workspace from `shell.lastWorkspaceId`, and
 * until `shell.bootstrap` has answered that key reads `null` — the store's default, not the
 * user's answer. Redirecting on it sent every cold start to the account's default workspace and
 * then *wrote that back*, so the choice the server held was overwritten by the fallback within a
 * second of the page loading.
 */
export function usePreferencesHydrated(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => hydrated,
    () => false,
  );
}

export function getPreferences(): ShellPreferences {
  return state;
}

export function setPreference<K extends PreferenceKey>(key: K, value: ShellPreferences[K]) {
  if (state[key] === value) return;
  state = { ...state, [key]: value };
  // P-170: the key, not the store.
  writeLocal(key, value);
  emit();

  /*
   * X2: **at most one request per 400 ms per key**, and the UI never waits on it — but the
   * *first* write of a burst goes now, not in 400 ms (a **leading** edge).
   *
   * A trailing-only debounce loses a value to any navigation inside its own window, which is
   * not hypothetical: choosing *Do not disturb* and reloading a second later left the server
   * with nothing, because the request had not been made yet and the reload cancelled it. A
   * deliberate choice is one write, so it should cost one request, immediately; the window
   * exists for the *streams* — the resizer's drag, a folder expanded repeatedly — and those
   * still coalesce into one more request at the end of it.
   */
  const send = () => {
    // A write that beat the bootstrap waits for it: `hydratePreferences` flushes the queue.
    if (!hydrated) {
      queuedRemote.add(key);
      return;
    }
    setRemotePreference(key, state[key]);
  };

  const existing = pending.get(key);
  if (existing) {
    // Inside the window: replace the trailing send with one carrying the newest value.
    clearTimeout(existing.timer);
    pending.set(key, {
      timer: setTimeout(() => {
        pending.delete(key);
        send();
      }, DEBOUNCE_MS),
    });
    return;
  }

  send();
  // The cooldown: while it is running, further writes of this key coalesce into one send.
  pending.set(key, { timer: setTimeout(() => pending.delete(key), DEBOUNCE_MS) });
}

/** Test-only: drop the store and every pending debounce. */
export function __resetPreferences() {
  for (const entry of pending.values()) clearTimeout(entry.timer);
  pending.clear();
  queuedRemote.clear();
  hydrated = false;
  state = { ...PREFERENCE_DEFAULTS };
  try {
    window?.localStorage?.removeItem(LOCAL_STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
  emit();
}

function subscribe(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** `[value, setValue]` — optimistic local write, debounced remote write (F4 §6). */
export function usePreference<K extends PreferenceKey>(
  key: K,
): [ShellPreferences[K], (value: ShellPreferences[K]) => void] {
  const value = useSyncExternalStore(
    subscribe,
    () => state[key],
    () => PREFERENCE_DEFAULTS[key],
  );
  const set = useCallback((next: ShellPreferences[K]) => setPreference(key, next), [key]);
  return [value, set];
}
